import React from 'react';
import '../../styles/components/AsperaForm.scss';
import { language } from '../../services/LanguageService';
import { AsperaError } from '../../classes/AsperaError';
import { AsperaAutocomplete } from '../AsperaAutocomplete';
import { tracking } from '../../services/TrackingService';
import { SubManager } from '../../classes/SubManager';
import { fromEvent } from 'rxjs';
import { carbonLightColors, fullPageModeClass } from '../../constants/constants';
import { numberKeyEvent, numberTranslations, observableTimeout } from '../../helpers/starterHelpers';
import { getEnvironmentVar, iterableToArray } from '../../helpers/helpers';
import { AsperaTimePicker } from '../AsperaTimePicker';
import { AsperaCodeSnippet } from '../AsperaCodeSnippet';
import { AsperaMultiCheckbox } from '../AsperaMultiCheckbox';
import { PasswordInput, TextInput, NumberInput, TextArea, Toggle, Select, SelectItem, Checkbox, RadioButtonGroup, RadioButton, FormGroup, Button } from '../Carbon';
import { AsperaTooltip } from '../AsperaTooltip';
import { AsperaAccordion, AsperaAccordionItem } from '../AsperaAccordion';
import { AsperaColorPicker } from '../AsperaColorPicker';
import { AsperaDatePicker } from '../AsperaDatePicker';
import { AsperaNumberUnitInput } from '../AsperaNumberUnitInput';
/**
 * React Component
 *
 * The AsperaForm provides a simple way to render a carbon compliant form by passing
 * in a formatted object and rendering a consistent form every time.
 *
 * See `AsperaFormType` for details on object or for declaring your own/
 *
 * @example
 * ```javascript
 * <AsperaForm formDetails={formDetails} />
 * ```
 */
export class AsperaForm extends React.Component {
    constructor() {
        super(...arguments);
        this.state = {
            /** Indicate if form should be disabled (controlled by external sources) */
            disabledByOverride: false,
            /** Indicate if form is dirty */
            dirtyForm: false,
        };
        this.wrapperClass = 'aspera-form-external-wrapper';
        this.buttonShadowClass = 'aspera-form-button-over-content';
        this.subManager = new SubManager();
        /** Default toggle props to use for basic usage */
        this.defaultToggleProps = {
            labelA: language.get('forms.off'),
            labelB: language.get('forms.on'),
        };
        /** Default radio button group props to use for basic usage */
        this.defaultRadioButtonGroupProps = {
            labelPosition: 'right',
            orientation: 'horizontal',
        };
        /** Handle date changes since it is handled differently than event onClick */
        this.handleOnDateChange = (date, value) => {
            this.handleOnChange({ target: { value: date || '' } }, value);
        };
        /** Handle number changes from carbon since it is handled differently than event onClick */
        this.handleOnNumberChange = (numberValue, value) => {
            this.handleOnChange({ target: { value: numberValue || '' } }, value);
        };
        /** Handle radio, checkbox and toggle changes from carbon since it is handled differently than event onClick */
        this.handleOnNonEventChange = (checked, value) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const formType = {
                toggle: 'Toggle Clicked',
                checkbox: 'Checkbox Clicked',
                radio: 'Radio Clicked',
            };
            tracking.track('User Form', {
                action: formType[value.type],
                field: value.label,
                data: value.pointer ? this.getFormDetailPointer(value.pointer) : undefined,
                successFlag: true,
                resultValue: value.invalid ? value.errorText : undefined,
            });
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            this.handleOnChange({ target: { value: checked } }, value);
        };
        /** Handle multi checkbox items since it is handled differently than event onClick */
        this.handleMultiCheckboxItems = (items, value) => {
            tracking.track('User Form', {
                action: 'Clicked multi checkbox item',
                field: value.label,
                data: value.pointer ? this.getFormDetailPointer(value.pointer) : undefined,
                successFlag: true,
            });
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            this.handleOnChange({ target: { value: items } }, value);
        };
        this.setFormDetailPointer = (pointer, valueToUse) => {
            const { formDetails } = this.props;
            if (pointer.indexOf('.') > -1) {
                const keys = pointer.split('.');
                if (keys[0] && keys[1] && typeof formDetails.model[keys[0]] === 'object') {
                    formDetails.model[keys[0]][keys[1]] = valueToUse;
                    return;
                }
            }
            formDetails.model[pointer] = valueToUse;
        };
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.getFormDetailPointer = (pointer) => {
            const { formDetails } = this.props;
            if (pointer.indexOf('.') > -1) {
                const keys = pointer.split('.');
                if (keys[0] && keys[1] && typeof formDetails.model[keys[0]] === 'object') {
                    return formDetails.model[keys[0]][keys[1]];
                }
            }
            return formDetails.model[pointer];
        };
        this.markFormAsDirty = () => {
            const { formDetails, ignoreDirtyRules } = this.props;
            if (!ignoreDirtyRules) {
                this.setState({ dirtyForm: true });
                tracking.setDirtyForm(formDetails.id);
            }
            else {
                this.setState({});
            }
        };
        this.handleOnChange = (event, value) => {
            const { formDetails, delayCheckOnChange } = this.props;
            let valueToUse = event.target.value;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if (event.imaginaryTarget)
                valueToUse = event.imaginaryTarget.value;
            if (value.pointer) {
                value.dirty = true;
                // eslint-disable-next-line eqeqeq
                if (['number', 'string', 'boolean'].includes(typeof valueToUse) && this.getFormDetailPointer(value.pointer) == valueToUse) {
                    value.dirty = false;
                }
                this.setFormDetailPointer(value.pointer, valueToUse);
            }
            value.invalid = !!this.getInvalidType(value, value.pointer ? this.getFormDetailPointer(value.pointer) : undefined);
            if (typeof value.onChange === 'function') {
                value.onChange(valueToUse, formDetails.model, !this.isFormInvalid);
            }
            if (typeof formDetails.onChange === 'function') {
                if (delayCheckOnChange) {
                    setTimeout(() => {
                        if (typeof formDetails.onChange === 'function') {
                            formDetails.onChange(formDetails.model, !this.isFormInvalid);
                        }
                    }, 500);
                }
                else {
                    formDetails.onChange(formDetails.model, !this.isFormInvalid);
                }
            }
            if (value.dirty) {
                this.markFormAsDirty();
            }
            this.triggerOnChangeWatch();
            this.subManager.setSubscription(observableTimeout(10).subscribe({
                next: () => {
                    this.triggerOnChangeWatch();
                },
                error: error => {
                    new AsperaError('AsperaForm: encountered error waiting to recheck form', error);
                },
            }));
        };
        this.handleOnBlur = (value) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const formType = {
                text: 'Field Filled',
                textarea: 'Textarea Filled',
                password: 'Password Filled',
                select: 'Item Selected',
            };
            tracking.track('User Form', {
                action: formType[value.type],
                field: value.label,
                data: value.pointer ? this.getFormDetailPointer(value.pointer) : undefined,
                successFlag: true,
                resultValue: value.invalid ? value.errorText : undefined,
            });
            value.focused = true;
            this.setState({});
        };
        this.triggerOnChangeWatch = () => {
            const { validFormSubject } = this.props;
            if (validFormSubject)
                validFormSubject.next(!this.isFormInvalid);
        };
        this.getInvalidText = (value, itemValue, invalidReason) => {
            const dirtyString = typeof itemValue === 'string' ? !!itemValue : false;
            if ((value.focused || value.instantTouch || dirtyString) && invalidReason === 'required') {
                return language.get('forms.requireItem');
            }
            else if (invalidReason === 'invalid-number') {
                return language.get('forms.invalidNumber');
            }
            else if ((value.dirty || value.instantTouch || dirtyString) && invalidReason === 'custom') {
                const errorText = typeof value.errorText === 'function' ? value.errorText(itemValue) : value.errorText;
                return errorText;
            }
            return undefined;
        };
        this.getComputedValue = (value, param) => {
            const foundValue = value[param];
            if (!foundValue) {
                return false;
            }
            else if (typeof foundValue === 'function') {
                return foundValue();
            }
            else {
                return foundValue;
            }
        };
        this.getInvalidType = (value, itemValue) => {
            var _a, _b;
            if (Array.isArray(itemValue) && value.type === 'multi-checkbox') {
                const hasOneChecked = !!itemValue.filter(item => item.checked)[0];
                if (this.getComputedValue(value, 'required') && !hasOneChecked) {
                    return 'required';
                }
                return undefined;
            }
            else if (this.getComputedValue(value, 'required') && (typeof itemValue !== 'boolean' && !itemValue)) {
                return 'required';
            }
            else if ((typeof itemValue === 'boolean' || itemValue) && typeof value.validationCheck === 'function') {
                return value.validationCheck(itemValue) ? undefined : 'custom';
            }
            else if (this.getComputedValue(value, 'required') && (value.type === 'checkbox' || value.type === 'toggle') && !itemValue) {
                return 'required';
            }
            else if ((value.type === 'number' || value.type === 'number-unit') && itemValue) {
                const numberValue = Number(itemValue);
                if ((((_a = value.numberProps) === null || _a === void 0 ? void 0 : _a.max) !== undefined && numberValue > value.numberProps.max) ||
                    (((_b = value.numberProps) === null || _b === void 0 ? void 0 : _b.min) !== undefined && numberValue < value.numberProps.min) ||
                    Number.isNaN(numberValue)) {
                    return 'invalid-number';
                }
            }
            return undefined;
        };
        this.keyEvent = (event) => {
            const element = event.target;
            if (!this.isFormInvalid && event.key === 'Enter' && element.tagName === 'INPUT') {
                this.finalSubmitCall();
            }
        };
        this.handleSettingDateTime = (value, date, time) => {
            let finalValue = '';
            const itemValue = value.pointer ? this.getFormDetailPointer(value.pointer) : undefined;
            let currentDate = itemValue ? new Date(itemValue) : new Date();
            if (isNaN(currentDate.getTime())) {
                currentDate = new Date();
            }
            if (date) {
                currentDate.setMonth(date.getMonth());
                currentDate.setDate(date.getDate());
                currentDate.setFullYear(date.getFullYear());
                finalValue = currentDate.toISOString();
            }
            else if (time) {
                currentDate.setHours(time.hour, time.minute, 0, 0);
                finalValue = currentDate.toISOString();
            }
            this.handleOnChange({ target: { value: finalValue } }, value);
        };
        this.finalSubmitCall = () => {
            const { formDetails } = this.props;
            formDetails.items.forEach(item => {
                if (item.pointer && !item.noWhiteSpaceTrim) {
                    const value = this.getFormDetailPointer(item.pointer);
                    if (typeof value === 'string') {
                        const newValue = value.trim();
                        if (value !== newValue) {
                            this.setFormDetailPointer(item.pointer, newValue);
                        }
                    }
                }
            });
            if (typeof formDetails.onSubmit === 'function') {
                formDetails.onSubmit(formDetails.model);
            }
        };
        this.handleResize = (safetyCall) => {
            const { fullPageWrapperId } = this.props;
            if (!fullPageWrapperId)
                return;
            const wrapper = document.querySelector(`#${fullPageWrapperId}`);
            const buttonWrapper = document.querySelector(`#${fullPageWrapperId} .form-action-wrapper`);
            const bodyFullPageMode = document.querySelector(`.${fullPageModeClass}`);
            if (!wrapper || !buttonWrapper)
                return;
            /** Indicate if shadow should be rendered.  Expects header (3rem) and the buttons (6rem) */
            const needsShadow = (window.innerHeight - 150) < wrapper.scrollHeight;
            const hasLeftNav = wrapper.offsetLeft > 0;
            buttonWrapper.style.left = bodyFullPageMode ? '0' : hasLeftNav ? '16rem' : '';
            needsShadow ? buttonWrapper.classList.add(this.buttonShadowClass) : buttonWrapper.classList.remove(this.buttonShadowClass);
            if (!safetyCall) {
                this.timer = setTimeout(() => {
                    this.handleResize(true);
                }, 100);
            }
        };
    }
    getMaxLength(type = 'TEXT', value) {
        if (type === 'TEXT') {
            return value.maxLength || getEnvironmentVar('max_character_text_length');
        }
        else if (type === 'TEXTAREA') {
            return value.maxLength || getEnvironmentVar('max_character_textarea_length');
        }
    }
    getDateFormatPlaceholder() {
        return (language.getCurrentLanguage().dateFormat || 'm/d/Y')
            .replace(/D/g, 'DDD')
            .replace(/d/g, 'DD')
            .replace(/M/g, 'MMM')
            .replace(/m/g, 'MM')
            .replace(/Y/g, 'YYYY')
            .replace(/y/g, 'YY');
    }
    getFormItem(value, formLabel, idToUse, tooltipData) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q;
        const { formDetails, light } = this.props;
        let itemValue = value.pointer ? this.getFormDetailPointer(value.pointer) : undefined;
        if (value.type === 'autocomplete' && ((_b = (_a = value.autoCompleteProps) === null || _a === void 0 ? void 0 : _a.initialItems) === null || _b === void 0 ? void 0 : _b.length) && !itemValue && value.pointer) {
            this.setFormDetailPointer(value.pointer, value.autoCompleteProps.initialItems);
        }
        const dateItem = itemValue ? new Date(itemValue) : undefined;
        const invalidReason = this.getInvalidType(value, itemValue);
        const invalidText = this.getInvalidText(value, itemValue, invalidReason);
        value.invalid = !!invalidReason;
        const dateProps = value.dateProps || {};
        switch (value.type) {
            case 'header':
                return React.createElement("div", { className: "aspera-form-header" },
                    React.createElement("div", { className: "form-header-text" }, value.label),
                    !!value.helperText && React.createElement("div", { className: "form-header-subtext" }, value.helperText));
            case 'sub-header':
                return React.createElement("div", { className: "aspera-form-sub-header" },
                    React.createElement("div", { className: "form-sub-header-text" }, value.label),
                    !!value.helperText && React.createElement("div", { className: "form-sub-header-subtext" }, value.helperText));
            case 'password':
                return React.createElement(PasswordInput, { onKeyUp: this.keyEvent, autoComplete: 'new-password', className: `${light ? 'light-mode' : ''}`, spellCheck: value.disableSpellcheck ? 'false' : undefined, maxLength: this.getMaxLength('TEXT', value), helperText: value.helperText, invalidText: invalidText, id: idToUse, invalid: !!invalidText, disabled: this.getComputedValue(value, 'disabled'), "data-auto": `form-item-password-${value.pointer}`, value: itemValue || '', labelText: value.tooltipContent ? '' : formLabel, required: this.getComputedValue(value, 'required'), placeholder: value.placeholder, hidePasswordLabel: language.get('forms.hidePassword'), showPasswordLabel: language.get('forms.showPassword'), tooltipAlignment: "end", onChange: (event) => {
                        this.handleOnChange(event, value);
                    }, onBlur: () => {
                        this.handleOnBlur(value);
                    } });
            case 'text':
                return React.createElement(TextInput, { onKeyUp: this.keyEvent, autoComplete: 'off', className: `${light ? 'light-mode' : ''}`, spellCheck: value.disableSpellcheck ? 'false' : undefined, maxLength: this.getMaxLength('TEXT', value), helperText: value.helperText, invalidText: invalidText, id: idToUse, invalid: !!invalidText, "data-auto": `form-item-text-${value.pointer}`, disabled: this.getComputedValue(value, 'disabled'), value: itemValue || '', labelText: value.tooltipContent ? '' : formLabel, required: this.getComputedValue(value, 'required'), placeholder: value.placeholder, onChange: (event) => {
                        this.handleOnChange(event, value);
                    }, onBlur: () => {
                        this.handleOnBlur(value);
                    } });
            case 'color':
                return React.createElement(AsperaColorPicker, { onKeyUp: this.keyEvent, autoComplete: 'off', light: light, maxLength: this.getMaxLength('TEXT', value), helperText: value.helperText, invalidText: invalidText, id: idToUse, invalid: !!invalidText, "data-auto": `form-item-text-${value.pointer}`, disabled: this.getComputedValue(value, 'disabled'), value: itemValue || '', labelText: value.tooltipContent ? '' : formLabel, required: this.getComputedValue(value, 'required'), placeholder: value.placeholder, onChange: (color) => {
                        this.handleOnChange({ target: { value: color } }, value);
                    }, onBlur: () => {
                        this.handleOnBlur(value);
                    }, autofillColors: this.autofillColors });
            case 'number':
                return React.createElement(NumberInput, { onKeyUp: this.keyEvent, iconDescription: language.get('forms.numberInfo'), autoComplete: 'off', className: `${light ? 'light-mode' : ''}`, helperText: value.helperText, maxLength: this.getMaxLength('TEXT', value), invalidText: invalidText || language.get('forms.invalidNumber'), id: idToUse, "data-auto": `form-item-text-${value.pointer}`, disabled: this.getComputedValue(value, 'disabled'), value: itemValue || '', label: value.tooltipContent ? '' : formLabel, allowEmpty: !this.getComputedValue(value, 'required'), max: ((_c = value.numberProps) === null || _c === void 0 ? void 0 : _c.max) || Number.MAX_SAFE_INTEGER, min: ((_d = value.numberProps) === null || _d === void 0 ? void 0 : _d.min) || 0, step: (_e = value.numberProps) === null || _e === void 0 ? void 0 : _e.step, placeholder: value.placeholder, translateWithId: numberTranslations, onChange: (_event, data) => {
                        this.handleOnNumberChange(String((data === null || data === void 0 ? void 0 : data.value) || ''), value);
                    }, onPaste: (event) => {
                        event.preventDefault();
                    }, onKeyPress: (event) => {
                        var _a, _b, _c;
                        numberKeyEvent(event, (_a = value.numberProps) === null || _a === void 0 ? void 0 : _a.min, (_b = value.numberProps) === null || _b === void 0 ? void 0 : _b.allowAnyText, (_c = value.numberProps) === null || _c === void 0 ? void 0 : _c.allowDecimal);
                    }, onBlur: () => {
                        this.handleOnBlur(value);
                    } });
            case 'number-unit':
                return React.createElement(AsperaNumberUnitInput, { onKeyUp: this.keyEvent, className: `${light ? 'light-mode' : ''}`, units: ((_f = value.numberProps) === null || _f === void 0 ? void 0 : _f.units) || [], helperText: value.helperText, maxLength: this.getMaxLength('TEXT', value), invalidText: invalidText || language.get('forms.invalidNumber'), id: idToUse, dataAuto: `form-item-text-${value.pointer}`, disabled: this.getComputedValue(value, 'disabled'), value: itemValue || '', label: value.tooltipContent ? '' : formLabel, allowEmpty: !this.getComputedValue(value, 'required'), max: ((_g = value.numberProps) === null || _g === void 0 ? void 0 : _g.max) || Number.MAX_SAFE_INTEGER, min: ((_h = value.numberProps) === null || _h === void 0 ? void 0 : _h.min) || 0, step: (_j = value.numberProps) === null || _j === void 0 ? void 0 : _j.step, placeholder: value.placeholder, onChange: (data) => {
                        this.handleOnNumberChange(data, value);
                    }, onBlur: () => {
                        this.handleOnBlur(value);
                    } });
            case 'textarea':
                return React.createElement(TextArea, { onKeyUp: this.keyEvent, autoComplete: 'off', className: `${light ? 'light-mode' : ''}`, spellCheck: value.disableSpellcheck ? 'false' : undefined, maxLength: this.getMaxLength('TEXTAREA', value), helperText: value.helperText, invalidText: invalidText, id: idToUse, invalid: !!invalidText, disabled: this.getComputedValue(value, 'disabled'), "data-auto": `form-item-textarea-${value.pointer}`, value: itemValue || '', labelText: value.tooltipContent ? '' : formLabel, required: this.getComputedValue(value, 'required'), placeholder: value.placeholder, onChange: (event) => {
                        this.handleOnChange(event, value);
                    }, onBlur: () => {
                        this.handleOnBlur(value);
                    } });
            case 'time':
                if (!value.timeProps) {
                    new AsperaError('AsperaForm: Type time requires timeProps to be set on FormItem', { field: value });
                }
                return React.createElement(AsperaTimePicker, { id: idToUse, className: `${light ? 'light-mode' : ''}`, helperText: value.helperText, required: this.getComputedValue(value, 'required'), disabled: this.getComputedValue(value, 'disabled'), labelText: value.tooltipContent ? '' : formLabel, showTimezone: (_k = value.timeProps) === null || _k === void 0 ? void 0 : _k.showTimezone, showAmPm: (_l = value.timeProps) === null || _l === void 0 ? void 0 : _l.showAmPm, initialValue: (_m = value.timeProps) === null || _m === void 0 ? void 0 : _m.initialValue, dataAuto: `form-item-time-picker-${value.pointer}`, onChange: data => {
                        var _a, _b;
                        if (typeof ((_a = value.timeProps) === null || _a === void 0 ? void 0 : _a.onChange) === 'function') {
                            (_b = value.timeProps) === null || _b === void 0 ? void 0 : _b.onChange(data);
                            this.setState({});
                        }
                        this.triggerOnChangeWatch();
                    } });
            case 'date':
                if (value.helperText) {
                    new AsperaError('AsperaForm: Type date does not support helperText', { field: value });
                }
                if (!value.placeholder) {
                    value.placeholder = this.getDateFormatPlaceholder();
                }
                return React.createElement(AsperaDatePicker, Object.assign({}, dateProps, { className: `${light ? 'light-mode' : ''}`, value: itemValue ? new Date(itemValue) : '', onChange: (date) => {
                        this.handleOnDateChange(date, value);
                    }, onKeyUp: this.keyEvent, invalidText: invalidText, id: idToUse, invalid: !!invalidText, disabled: this.getComputedValue(value, 'disabled'), "data-auto": `form-item-date-${value.pointer}`, labelText: value.tooltipContent ? '' : formLabel, required: this.getComputedValue(value, 'required'), placeholder: value.placeholder, onBlur: () => {
                        this.handleOnBlur(value);
                    } }));
            case 'date-time':
                if (!value.placeholder) {
                    value.placeholder = this.getDateFormatPlaceholder();
                }
                return React.createElement("div", null,
                    React.createElement(AsperaDatePicker, Object.assign({}, dateProps, { className: `${light ? 'light-mode' : ''}`, value: itemValue ? new Date(itemValue) : '', onChange: (date) => {
                            this.handleSettingDateTime(value, date ? new Date(date) : undefined);
                        }, onKeyUp: this.keyEvent, invalidText: invalidText, id: idToUse, invalid: !!invalidText, disabled: this.getComputedValue(value, 'disabled'), "data-auto": `form-item-date-${value.pointer}`, labelText: value.tooltipContent ? '' : formLabel, required: this.getComputedValue(value, 'required'), placeholder: value.placeholder, onBlur: () => {
                            this.handleOnBlur(value);
                        } })),
                    React.createElement(AsperaTimePicker, { id: idToUse, className: `${light ? 'light-mode' : ''}`, required: this.getComputedValue(value, 'required'), disabled: this.getComputedValue(value, 'disabled'), labelText: '', helperText: value.helperText, showTimezone: (_o = value.timeProps) === null || _o === void 0 ? void 0 : _o.showTimezone, showAmPm: (_p = value.timeProps) === null || _p === void 0 ? void 0 : _p.showAmPm, initialValue: dateItem ? {
                            hour: dateItem.getHours(),
                            minute: dateItem.getMinutes(),
                        } : undefined, dataAuto: `form-item-time-picker-${value.pointer}`, onChange: data => {
                            this.handleSettingDateTime(value, undefined, data);
                        } }));
            case 'toggle':
                if (value.errorText) {
                    new AsperaError('AsperaForm: Type toggle does not support errorText', { field: value });
                }
                if (value.placeholder) {
                    new AsperaError('AsperaForm: Type toggle does not support placeholder', { field: value });
                }
                value.focused = true;
                return React.createElement("div", { className: "toggle-wrapper" },
                    React.createElement(Toggle, Object.assign({ id: idToUse, disabled: this.getComputedValue(value, 'disabled'), toggled: itemValue || false, labelText: value.tooltipContent ? ' ' : value.label, hideLabel: (_q = value.toggleProps) === null || _q === void 0 ? void 0 : _q.hideLabel, required: this.getComputedValue(value, 'required'), "data-auto": `form-item-toggle-${value.pointer}`, placeholder: value.placeholder, onToggle: (checked) => {
                            this.handleOnNonEventChange(checked, value);
                        } }, (value.toggleProps || this.defaultToggleProps))),
                    !!value.helperText && React.createElement("div", { className: "cds--form__helper-text" }, value.helperText));
            case 'select':
                if (!Array.isArray(value.items) || !value.items.length) {
                    new AsperaError('AsperaForm: Type select requires items to be an array of choices', { field: value });
                    value.items = [];
                }
                if (value.placeholder) {
                    new AsperaError('AsperaForm: Type select does not support placeholder', { field: value });
                }
                if (Array.isArray(value.items)) {
                    const hasProperValue = !!(value.items.filter(item => item.value === itemValue).length);
                    if (!hasProperValue && itemValue) {
                        itemValue = undefined;
                    }
                }
                return React.createElement(Select, { onKeyUp: this.keyEvent, "data-auto": `form-item-select-${value.pointer}`, className: `${light ? 'light-mode' : ''}`, helperText: value.helperText, invalidText: invalidText, id: idToUse, invalid: !!invalidText, disabled: this.getComputedValue(value, 'disabled'), defaultValue: itemValue, labelText: value.tooltipContent ? '' : formLabel, required: this.getComputedValue(value, 'required'), onChange: (event) => {
                        this.handleOnChange(event, value);
                    }, onBlur: () => {
                        this.handleOnBlur(value);
                    } },
                    React.createElement(SelectItem, { text: value.selectDefaultText || language.get('forms.chooseOption'), value: "", "data-auto": "form-item-select-item-unset" }),
                    Array.isArray(value.items) ? value.items.filter(item => !item.hidden).map((item, index) => {
                        return React.createElement(SelectItem, { key: index, disabled: item.disabled, value: item.value, text: item.text, "data-auto": `form-item-select-item-${item.value}` });
                    }) : '');
            case 'checkbox':
                if (value.errorText) {
                    new AsperaError('AsperaForm: Type checkbox does not support errorText', { field: value });
                }
                if (value.placeholder) {
                    new AsperaError('AsperaForm: Type checkbox does not support placeholder', { field: value });
                }
                value.focused = true;
                return React.createElement("div", { className: "checkbox-wrapper" },
                    !!(value.legendLabel && !value.tooltipContent) && React.createElement("legend", { className: "cds--label" }, formLabel),
                    React.createElement(Checkbox, { "data-auto": `form-item-checkbox-${value.pointer}`, id: idToUse, disabled: this.getComputedValue(value, 'disabled'), checked: itemValue || false, labelText: value.tooltipContent ? tooltipData : value.label || '', required: this.getComputedValue(value, 'required'), onChange: (_event, data) => {
                            this.handleOnNonEventChange(data.checked, value);
                        } }),
                    !!value.helperText && React.createElement("div", { className: "cds--form__helper-text" }, value.helperText));
            case 'multi-checkbox':
                if (value.placeholder) {
                    new AsperaError('AsperaForm: Type multi checkbox does not support placeholder', { field: value });
                }
                return React.createElement("div", { className: "multi-checkbox-wrapper" },
                    React.createElement(AsperaMultiCheckbox, { dataAuto: `form-item-checkbox-${value.pointer}`, id: idToUse, items: Array.isArray(itemValue) ? itemValue : [], disabled: this.getComputedValue(value, 'disabled'), helperText: value.helperText, labelText: value.tooltipContent ? '' : formLabel, required: this.getComputedValue(value, 'required'), invalidText: invalidText, invalid: !!invalidText, onChange: (items) => {
                            value.focused = true;
                            this.handleMultiCheckboxItems(items, value);
                        } }));
            case 'radio':
                if (!Array.isArray(value.items) || !value.items.length) {
                    new AsperaError('AsperaForm: Type radio requires items to be an array of choices', { field: value });
                    value.items = [];
                }
                if (value.placeholder) {
                    new AsperaError('AsperaForm: Type radio does not support placeholder', { field: value });
                }
                if (Array.isArray(value.items)) {
                    const hasProperValue = !!(value.items.filter(item => item.value === itemValue).length);
                    if (!hasProperValue && itemValue) {
                        new AsperaError('AsperaForm: Type radio was loaded with a value that was not selectable', { field: value });
                        itemValue = undefined;
                    }
                }
                return React.createElement("div", { className: "radio-wrapper" },
                    !!(value.legendLabel && !value.tooltipContent) && React.createElement("legend", { className: "cds--label" }, formLabel),
                    React.createElement(RadioButtonGroup, Object.assign({ "data-auto": `form-item-radio-${value.pointer}`, defaultSelected: itemValue, name: idToUse, onChange: (selected) => {
                            this.handleOnNonEventChange(selected, value);
                        }, valueSelected: itemValue }, (value.radioProps || this.defaultRadioButtonGroupProps)), Array.isArray(value.items) ? value.items.map((item, index) => {
                        return React.createElement(RadioButton, { key: index, id: `${idToUse}-Radio-${index}`, disabled: item.disabled, value: item.value, labelText: item.text, "data-auto": `form-item-radio-${value.pointer}-${item.value}` });
                    }) : ''),
                    !!value.helperText && React.createElement("div", { className: "cds--form__helper-text" }, value.helperText));
            case 'autocomplete':
                if (value.autoCompleteProps) {
                    const props = Object.assign({}, value.autoCompleteProps, {
                        required: this.getComputedValue(value, 'required'),
                        disabled: this.getComputedValue(value, 'disabled'),
                        label: value.tooltipContent ? '' : formLabel,
                        onChange: (items) => {
                            var _a;
                            if (value.pointer)
                                this.setFormDetailPointer(value.pointer, items);
                            value.dirty = true;
                            this.triggerOnChangeWatch();
                            this.markFormAsDirty();
                            if (typeof ((_a = value.autoCompleteProps) === null || _a === void 0 ? void 0 : _a.onChange) === 'function')
                                value.autoCompleteProps.onChange(items);
                            if (typeof value.onChange === 'function')
                                value.onChange(items, formDetails.model, !this.isFormInvalid);
                        },
                    });
                    return (React.createElement(AsperaAutocomplete, Object.assign({}, props, { light: light, "data-auto": `form-item-autocomplete-${value.pointer}` })));
                }
                else {
                    new AsperaError('AsperaForms: Type autocomplete missing required AutocompleteProps');
                    break;
                }
            case 'code':
                return (React.createElement(AsperaCodeSnippet, Object.assign({ label: value.label }, (value.codeSnippetProps || { type: 'single' }), { value: itemValue, light: light, "data-auto": `form-item-code-snippet-${value.pointer}` })));
            case 'custom':
                return value.customView || '';
        }
    }
    get isFormInvalid() {
        const { formDetails } = this.props;
        const { disabledByOverride } = this.state;
        if (disabledByOverride) {
            return true;
        }
        let invalidAccordionChildren = false;
        formDetails.items.filter(value => value.type === 'accordion').forEach(value => {
            var _a;
            if (!invalidAccordionChildren)
                invalidAccordionChildren = !!((_a = value.accordionItems) === null || _a === void 0 ? void 0 : _a.filter(value => !this.getComputedValue(value, 'hidden')).filter(value => value.invalid).length);
        });
        return (invalidAccordionChildren || !!formDetails.items.filter(value => !this.getComputedValue(value, 'hidden')).filter(value => value.invalid).length);
    }
    getFormGroupItem(value, index) {
        const { formDetails, light } = this.props;
        const noTooltipLabelTypes = ['checkbox', 'header', 'sub-header'];
        const noOptionalLabelTypes = ['checkbox', 'radio', 'toggle'];
        let formLabel = value.legendLabel || value.label || '';
        const idToUse = `AsperaForm-${formDetails.id}-Element-${index}`;
        if (formLabel &&
            !this.getComputedValue(value, 'required') &&
            noOptionalLabelTypes.indexOf(value.type) === -1 &&
            !value.hideOptional &&
            !this.getComputedValue(value, 'disabled')) {
            formLabel = `${formLabel} (${language.get('forms.optional')})`;
        }
        const tooltipData = React.createElement("div", { className: `aspera-form-tooltip-label ${value.type === 'checkbox' ? 'checkbox-label' : ''}` },
            value.type === 'checkbox' ? formLabel : React.createElement("label", { htmlFor: idToUse, className: "cds--label" }, formLabel),
            React.createElement(AsperaTooltip, Object.assign({}, ((value === null || value === void 0 ? void 0 : value.tooltipProps) || {})), value.tooltipContent));
        return (React.createElement(FormGroup, { legendText: "", key: index, className: `${this.getComputedValue(value, 'collapseGroup') ? 'collapse-group' : ''} ${value.type === 'radio' ? 'radio-fieldset' : ''} ${value.type === 'header' || value.type === 'sub-header' ? 'header-fieldset' : ''} ${light ? 'light-mode' : ''}` },
            !!(value.tooltipContent && noTooltipLabelTypes.indexOf(value.type) === -1) && tooltipData,
            this.getFormItem(value, formLabel, idToUse, tooltipData)));
    }
    get submitButtonText() {
        const { fullPageWrapperId } = this.props;
        const createModeBody = document.querySelector(`.${fullPageModeClass}`);
        if (fullPageWrapperId && createModeBody) {
            return language.get('forms.create');
        }
        else if (fullPageWrapperId && !createModeBody) {
            return language.get('forms.save');
        }
        else {
            return language.get('forms.submit');
        }
    }
    get autofillColors() {
        const { autofillColors, formDetails } = this.props;
        const foundColors = new Map();
        formDetails.items.forEach(item => {
            if (item.type === 'color' && typeof formDetails.model === 'object' && item.pointer) {
                const value = formDetails.model[item.pointer];
                if (value && typeof value === 'string' && value.includes('#')) {
                    foundColors.set(value, value);
                }
            }
        });
        (autofillColors || []).forEach(item => foundColors.set(item, item));
        carbonLightColors.forEach(item => foundColors.set(item, item));
        return iterableToArray(foundColors.values()).slice(0, 16);
    }
    get formContent() {
        const { formDetails } = this.props;
        const { dirtyForm } = this.state;
        const submit = () => {
            this.setState({ dirtyForm: false });
            tracking.removeDirtyForm(formDetails.id);
            this.finalSubmitCall();
        };
        const cancel = () => {
            this.setState({ dirtyForm: false });
            tracking.removeDirtyForm(formDetails.id);
            if (typeof formDetails.onCancel === 'function') {
                formDetails.onCancel();
            }
        };
        const formGroupItems = [];
        const nonHiddenFormItems = formDetails.items.filter(value => !this.getComputedValue(value, 'hidden'));
        nonHiddenFormItems.forEach((value, index) => {
            if (value.type === 'accordion') {
                let hasAccordionSibling = false;
                if (!Array.isArray(value.accordionItems))
                    throw new AsperaError('AsperaForm: Accordion used with no accordionItems').getError();
                if (nonHiddenFormItems[index + 1] && nonHiddenFormItems[index + 1].type === 'accordion')
                    hasAccordionSibling = true;
                formGroupItems.push(React.createElement(AsperaAccordion, { key: index },
                    React.createElement(AsperaAccordionItem, { open: this.getComputedValue(value, 'accordionOpen'), title: value.label, className: `aspera-form-accordion-group ${hasAccordionSibling ? 'has-accordion-sibling' : ''}` }, value.accordionItems.filter(value => !this.getComputedValue(value, 'hidden')).map((accordionValue, accordionIndex) => {
                        if (accordionValue.type === 'accordion')
                            throw new AsperaError('AsperaForm: accordion type cannot be a child of an accordion').getError();
                        return this.getFormGroupItem(accordionValue, `${index}-${accordionIndex}`);
                    }))));
            }
            else {
                formGroupItems.push(this.getFormGroupItem(value, String(index)));
            }
        });
        return (React.createElement(React.Fragment, null,
            formGroupItems.map(item => item),
            !!(formDetails.onCancel || formDetails.onSubmit) && React.createElement("div", { className: "form-action-wrapper" },
                React.createElement("div", { className: "form-action-content" },
                    formDetails.onCancel ? React.createElement(Button, { className: "form-buttons", kind: "secondary", type: "button", "data-auto": "form-cancel-button", onClick: cancel }, formDetails.cancelText ? formDetails.cancelText : language.get('forms.cancel')) : '',
                    formDetails.onSubmit ? React.createElement(Button, { className: "form-buttons", kind: "primary", "data-auto": "form-submit-button", disabled: this.isFormInvalid, type: "button", onClick: submit }, formDetails.submitText ? formDetails.submitText : this.submitButtonText) : '',
                    dirtyForm && React.createElement("span", { className: "form-buttons--text" }, language.get('forms.unsavedChanges'))))));
    }
    handleWrapperID(remove) {
        const { fullPageWrapperId } = this.props;
        const wrapper = document.querySelector(`#${fullPageWrapperId}`);
        if (!wrapper) {
            new AsperaError('AsperaForm: unable to find wrapper by ID', { id: fullPageWrapperId });
            return;
        }
        remove ? wrapper.classList.remove(this.wrapperClass) : wrapper.classList.add(this.wrapperClass);
    }
    componentDidMount() {
        const { fullPageWrapperId, disableOverride, externalDirty } = this.props;
        if (disableOverride) {
            this.subManager.setSubscription(disableOverride.subscribe({
                next: disable => {
                    this.setState({ disabledByOverride: disable });
                },
                error: error => {
                    new AsperaError('AsperaForm: unable to subscribe to disable override', error);
                },
            }));
        }
        if (externalDirty) {
            this.subManager.setSubscription(externalDirty.subscribe({
                next: () => {
                    this.markFormAsDirty();
                },
                error: error => {
                    new AsperaError('AsperaForm: unable to subscribe to disable override', error);
                },
            }));
        }
        if (fullPageWrapperId) {
            this.handleWrapperID();
            this.handleResize();
            this.subManager.setSubscription(fromEvent(window, 'resize').subscribe({
                next: () => {
                    this.handleResize();
                },
                error: error => {
                    new AsperaError('AsperaForm: unable to subscribe to resize event', error);
                },
            }));
        }
        this.subManager.setSubscription(observableTimeout().subscribe({
            next: () => {
                this.triggerOnChangeWatch();
            },
            error: error => {
                new AsperaError('AsperaForm: unable to wait for initial trigger change', error);
            },
        }));
    }
    componentDidUpdate() {
        this.handleResize();
    }
    componentWillUnmount() {
        const { fullPageWrapperId, formDetails } = this.props;
        if (fullPageWrapperId) {
            this.handleWrapperID(true);
        }
        if (this.timer)
            clearTimeout(this.timer);
        tracking.removeDirtyForm(formDetails.id);
        this.subManager.unsubscribeAll();
    }
    render() {
        const { className, formDetails } = this.props;
        return React.createElement("div", { id: `AsperaForm-${formDetails.id}`, className: `aspera-form ${className ? className : ''}`, "data-auto": `form-wrapper-${formDetails.id}` }, this.formContent);
    }
}
